/**
 * atoms.tsx — small shared presentational pieces for the widget cards.
 * All purely presentational; they take already-formatted/validated props.
 */
import React from 'react'
import { StarIcon, CalendarIcon, DollarIcon } from '../icons'
import { cx } from '../cx'
import { StatusTone, WidgetDomain } from './format'
import styles from './widgets.module.scss'

/** the white glyph shown on a domain's coloured tile / chip dot. */
export const domainGlyph = (domainKey: WidgetDomain['key'], className?: string): React.ReactNode => {
  if (domainKey === 'bookings') return <CalendarIcon className={className} />
  if (domainKey === 'payments') return <DollarIcon className={className} />
  return <StarIcon className={className} filled />
}

/** 0–5 star row (gold, a universally-read rating affordance) with an optional value. */
export const Stars: React.FC<{ count: number; value?: string }> = ({ count, value }) => (
  <span className={styles.stars} aria-label={`${count} out of 5`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <StarIcon key={i} className={styles.star} filled={i < count} />
    ))}
    {value ? <span className={styles.starsValue}>{value}</span> : null}
  </span>
)

/** semantic status pill (paid/upcoming/cancelled → positive/pending/negative). */
export const StatusBadge: React.FC<{ label: string; tone: StatusTone }> = ({ label, tone }) => (
  <span className={cx(styles.status, styles[`status_${tone}` as keyof typeof styles])}>{label}</span>
)

/** card header: a domain-tinted icon tile + label, and an optional right-side metric. */
export const CardHeader: React.FC<{
  color: string
  label: string
  glyph: React.ReactNode
  metric?: React.ReactNode
}> = ({ color, label, glyph, metric }) => (
  <div className={styles.cardHead}>
    <span className={styles.cardTile} style={{ background: color }}>
      {glyph}
    </span>
    <span className={styles.cardLabel}>{label}</span>
    {metric != null && <span className={styles.cardMetric}>{metric}</span>}
  </div>
)
